import {Accordion, AccordionContent, AccordionTitle, Icon} from "semantic-ui-react";
import React, {useEffect, useState} from "react";
import VehicleCard from "../components/VehicleCard";
import imageRegistry from "../constants/imageRegistry";
import {ReactSVG} from "react-svg";
import {useMutation, useQuery} from "@tanstack/react-query";
import {FETCH_VEHICLES} from "../react-query/QueryConstant";
import {getVehicles, updateVehicleEnrollment} from "../services/Vehicles";
import queryClient from "../react-query/QueryClient";
import AddVehicleModal from "../components/AddVehicle";
import {notifyToaster} from "../helpers/common";
import {TOASTIFY_STATUS} from "../constants/application";
import {useNavigationHandler} from "../contexts/Navigation";
import {useNavigate} from "react-router";
import {routerPaths} from "../utils/routerPaths";
import {useSelector} from "react-redux";
import {getPersonalInfo} from "../redux/slices/PersonalAccountInformationSlice";
import {gaJSON, useTrackFirebaseAnalytics} from "../utils/googleAnalytics";
import {skipOnboarding} from "../services/OnboardingSteps";

const EnrollVehicles = () => {
    const [activeIndex, setActiveIndex] = useState([]);
    const [spareCards, setSpareCards] = useState([]);
    const [assignedCards, setAssignedCards] = useState([]);
    const [enrollLaterCards, setEnrollLaterCards] = useState([]);
    const [pendingCards, setPendingCards] = useState([]);
    const [currentCard, setCurrentCard] = useState(null);
    const [vehicle, setVehicle] = useState(null);

    const navigate = useNavigate();

    const {
        handleContinueDisabled,
        registerCallbackFunction,
        setDisabledTooltip
    } = useNavigationHandler();

    const handleClick = (e, titleProps) => {
        const {index} = titleProps
        setActiveIndex((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    }

    const {fleet_id} = useSelector((state) => getPersonalInfo(state)) || {};

    const {data: vehiclesData, isLoading} = useQuery({
        queryKey: [FETCH_VEHICLES, fleet_id],
        queryFn: () => getVehicles({fleetId: fleet_id}),
        enabled: !!fleet_id,
    });

    useEffect(() => {
        if (isLoading) {
            setDisabledTooltip("Vehicles are loading. Please wait.")
            handleContinueDisabled(true);
        }
    }, [isLoading]);

    const track = useTrackFirebaseAnalytics();

    useEffect(() => {
        if (vehiclesData?.result.length) {
            track(gaJSON.onboarding_screen_viewed, {step_name: "enroll-vehicles", card_count: vehiclesData?.result.length})
        }
    }, [vehiclesData]);

    const {mutate: updateVehicle} = useMutation({
        mutationFn: updateVehicleEnrollment,
        onSuccess: (data) => {
            queryClient.invalidateQueries(FETCH_VEHICLES);
            setCurrentCard(null)
            setVehicle(null)
            let successMessage = "test";

            if (data.result.enroll_code === 'spare') {
                successMessage = "Card marked as spare";
            } else if (data.result.enroll_code === 'enrolled') {
                successMessage = "Vehicle Successfully enrolled!";
            } else if (data.result.enroll_code === 'later') {
                successMessage = "Vehicle to be enrolled later";
            }

            notifyToaster(successMessage, {
                type: data.result.enroll_code === 'later' ? TOASTIFY_STATUS.warning : TOASTIFY_STATUS.success,
                hideProgressBar: true,
                theme: "colored",
            });
        },
        onError: (error) => {
            console.log(error);
        }
    });

    const updateVehicleStatus = ({vehicle, enroll_code}) => {
        updateVehicle({vehicleId: vehicle.id, fleetId: fleet_id, enroll_code: enroll_code})
    }

    useEffect(() => {
        const vehicles = vehiclesData?.result;
        if (!vehicles) return;

        const pending = [], later = [], spare = [], assigned = [];

        vehicles.forEach((vehicle) => {
            switch (vehicle.enroll_code) {
                case "pending":
                    pending.push(vehicle);
                    break;
                case "later":
                    later.push(vehicle);
                    break;
                case "spare":
                    spare.push(vehicle);
                    break;
                case "enrolled":
                    assigned.push(vehicle);
                    break;
                default:
                    break;
            }
        });

        setPendingCards(pending);
        setEnrollLaterCards(later);
        setSpareCards(spare);
        setAssignedCards(assigned);

        if (pending.length > 0) {
            handleContinueDisabled(true);
            setDisabledTooltip("All cards must be assigned to continue.")
        } else {
            setDisabledTooltip(null)
            handleContinueDisabled(false);
            track(gaJSON.onboarding_all_cards_assigned,{
                card_count:vehiclesData?.result.length,
                enroll_later: later.length,
                spare: spare.length,
                assigned: assigned.length
            })
        }
    }, [vehiclesData]);

    useEffect(() => {
        registerCallbackFunction(() => () => {
            skipOnboarding()
            navigate(`${routerPaths.downloadMembershipCard}`)
        });
        return () => setDisabledTooltip(null);
    }, [])

    return <div className="enroll-vehicles">
        <div>
            <p className="step">STEP 1 of 2</p>
            <p className="step-title">Enroll Vehicles</p>
        </div>
        <p className="description">Assign a vehicle for each Fuelman Card to schedule service. All
            cards must be assigned a vehicle, marked as spare, or tagged to assign later. Vehicles can be assigned later
            in your online account, but at least one vehicle must be assigned to complete onboarding.</p>
        {(pendingCards.length > 0 || isLoading) && <div className="card-block">
            <p className="title">Cards Awaiting Assignment ({pendingCards.length})</p>
            <div className="cards-container">
                {pendingCards.map((card) => (
                    <VehicleCard key={card.id} vehicle={card} updateVehicleStatus={updateVehicleStatus}
                                 setCurrentCard={setCurrentCard}/>
                ))}
            </div>
        </div>}
        {pendingCards.length === 0 && !isLoading && <div className="card-block-success">
            <ReactSVG src={imageRegistry.greenRight}/>
            <p className="title">All cards successfully assigned!</p>
            <p className="description-muted">Changes and additional vehicles enrollments can be made
                anytime in your online account.</p>
        </div>}
        <Accordion className="card-box">
            <AccordionTitle
                active={activeIndex.includes(1)}
                index={1}
                onClick={handleClick}
                className="card-accordion-title"
            >
                <div>
                    <p className="title">Assigned Cards and Enrolled Vehicles ({assignedCards.length})</p>
                    <p className="title-16px">Vehicles are assigned and ready to schedule maintenance.</p>
                </div>
                <Icon name='dropdown'/>
            </AccordionTitle>
            <AccordionContent active={activeIndex.includes(1)}>
                {assignedCards.length === 0 && <div className="card-accordion-no-content">
                    <ReactSVG className="info-svg" src={imageRegistry.GrayInfoLogo}/>
                    <p className="description-muted">No Assigned Cards And Vehicles</p>
                </div>}
                <div className="cards-container">
                    {assignedCards.map((card) => <VehicleCard key={card.id} vehicle={card}
                                                              updateVehicleStatus={updateVehicleStatus}
                                                              setCurrentCard={setCurrentCard}/>)}
                </div>
            </AccordionContent>
        </Accordion>
        <Accordion className="card-box">
            <AccordionTitle
                active={activeIndex.includes(2)}
                index={2}
                onClick={handleClick}
                className="card-accordion-title">
                <div>
                    <p className="title">Enroll Later ({enrollLaterCards.length})</p>
                    <p className="title-14px">These cards do not have a vehicle assigned. Vehicles cannot receive
                        maintenance until enrolled
                        and
                        assigned to a card. Enroll and assign vehicles anytime through your online account.</p>
                </div>
                <Icon name='dropdown'/>
            </AccordionTitle>
            <AccordionContent active={activeIndex.includes(2)}>
                {enrollLaterCards.length === 0 && <div className="card-accordion-no-content">
                    <ReactSVG className="info-svg" src={imageRegistry.GrayInfoLogo}/>
                    <p className="description-muted">No Unassigned Cards</p>
                </div>}
                <div className="cards-container">
                    {enrollLaterCards.map((card) => <VehicleCard key={card.id} vehicle={card}
                                                                 updateVehicleStatus={updateVehicleStatus}
                                                                 setCurrentCard={setCurrentCard}/>)}
                </div>
            </AccordionContent>
        </Accordion>
        <Accordion className="card-box">
            <AccordionTitle
                active={activeIndex.includes(3)}
                index={3}
                onClick={handleClick}
                className="card-accordion-title"
            >
                <div>
                    <p className="title">Spare Cards ({spareCards.length})</p>
                    <p className="title-16px">Use spare cards for new vehicles or as backups if you lose a card.
                        Enroll and assign vehicles
                        anytime
                        through your online account.</p>
                </div>
                <Icon name='dropdown'/>
            </AccordionTitle>
            <AccordionContent active={activeIndex.includes(3) === true}>
                {spareCards.length === 0 && <div className="card-accordion-no-content">
                    <ReactSVG className="info-svg" src={imageRegistry.GrayInfoLogo}/>
                    <p className="description-muted">No Spare Cards</p>
                </div>}
                <div className="cards-container">
                    {spareCards.map((card) => <VehicleCard key={card.id} vehicle={card}
                                                           updateVehicleStatus={updateVehicleStatus}
                                                           setCurrentCard={setCurrentCard}/>)}
                </div>
            </AccordionContent>
        </Accordion>
        <AddVehicleModal card={currentCard} setCard={setCurrentCard} updateVehicleStatus={updateVehicleStatus} vehicle={vehicle} setVehicle={setVehicle} vehicles={vehiclesData}/>
    </div>
}

export default EnrollVehicles;
